import { BolsoLayout } from "@/components/BolsoLayout";
import { TransactionForm } from "@/components/TransactionForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { CheckCircle2, Loader2, Pencil, Plus, Target, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../server/routers";
import { useI18n } from "@/contexts/I18nContext";
type Goal = inferRouterOutputs<AppRouter>["goals"]["list"][number];

const GOAL_COLORS = ["#3d8b3d","#2196F3","#FF9800","#E91E63","#9C27B0","#00BCD4"];
const GOAL_ICONS = ["🎯","🏠","✈️","🚗","📚","💰","💍","🏋️","🎓","🌟"];

export default function Goals() {
  const { t, ui, formatCurrency, formatDate, currencySymbol, categoryName } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [name, setName] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [currentAmount, setCurrentAmount] = useState("");
  const [deadline, setDeadline] = useState("");
  const [icon, setIcon] = useState("🎯");
  const [color, setColor] = useState(GOAL_COLORS[0]);
  const [linkedCategoryId, setLinkedCategoryId] = useState<string>("");

  const [addingTo, setAddingTo] = useState<number | null>(null);
  const [addAmount, setAddAmount] = useState("");
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [editName, setEditName] = useState("");
  const [editTargetAmount, setEditTargetAmount] = useState("");
  const [editCurrentAmount, setEditCurrentAmount] = useState("");
  const [editDeadline, setEditDeadline] = useState("");
  const [editIcon, setEditIcon] = useState("🎯");
  const [editColor, setEditColor] = useState(GOAL_COLORS[0]);
  const [editLinkedCategoryId, setEditLinkedCategoryId] = useState<string>("");

  const { data: goals = [], isLoading } = trpc.goals.list.useQuery({});
  const { data: categories = [] } = trpc.categories.list.useQuery({});
  const utils = trpc.useUtils();

  const createMutation = trpc.goals.create.useMutation({
    onSuccess: () => {
      toast.success(ui.goals.created);
      utils.goals.list.invalidate();
      setShowGoalForm(false);
      resetForm();
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.goals.update.useMutation({
    onSuccess: () => {
      toast.success(ui.goals.updated);
      utils.goals.list.invalidate();
      setAddingTo(null);
      setAddAmount("");
      setEditingGoal(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const openEditDialog = (goal: Goal) => {
    setEditingGoal(goal);
    setEditName(goal.name);
    setEditTargetAmount(String(parseFloat(String(goal.targetAmount))));
    setEditCurrentAmount(String(parseFloat(String(goal.currentAmount))));
    setEditDeadline(goal.deadline ? new Date(goal.deadline).toISOString().split("T")[0] : "");
    setEditIcon(goal.icon ?? "🎯");
    setEditColor(goal.color ?? GOAL_COLORS[0]);
    setEditLinkedCategoryId(goal.linkedCategoryId ? String(goal.linkedCategoryId) : "");
  };

  const handleEditSave = () => {
    if (!editingGoal) return;
    const target = parseFloat(editTargetAmount.replace(",", "."));
    const current = parseFloat(editCurrentAmount.replace(",", ".")) || 0;
    if (!editName || !target) return toast.error(ui.goals.required);
    updateMutation.mutate({
      id: editingGoal.id,
      name: editName,
      targetAmount: target,
      currentAmount: current,
      deadline: editDeadline ? new Date(editDeadline) : undefined,
      icon: editIcon,
      color: editColor,
      linkedCategoryId: editLinkedCategoryId ? parseInt(editLinkedCategoryId) : null,
    });
  };

  const deleteMutation = trpc.goals.delete.useMutation({
    onSuccess: () => { toast.success(ui.goals.deleted); utils.goals.list.invalidate(); },
    onError: () => toast.error(ui.goals.deleteError),
  });

  const resetForm = () => {
    setName(""); setTargetAmount(""); setCurrentAmount(""); setDeadline("");
    setIcon("🎯"); setColor(GOAL_COLORS[0]); setLinkedCategoryId("");
  };

  const handleCreate = () => {
    const target = parseFloat(targetAmount.replace(",", "."));
    if (!name || !target) return toast.error(ui.goals.required);
    createMutation.mutate({
      name, targetAmount: target,
      deadline: deadline ? new Date(deadline) : undefined,
      icon, color,
      linkedCategoryId: linkedCategoryId ? parseInt(linkedCategoryId) : undefined,
    });
  };

  const handleAddAmount = (goalId: number, current: number) => {
    const add = parseFloat(addAmount.replace(",", "."));
    if (!add || add <= 0) return toast.error(ui.goals.invalidAmount);
    updateMutation.mutate({ id: goalId, currentAmount: current + add });
  };

  return (
    <BolsoLayout onNewTransaction={() => setShowForm(true)}>
      <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif font-bold text-foreground">{t.goals.title}</h1>
          <Button onClick={() => setShowGoalForm(true)} size="sm" className="gap-1.5">
            <Plus className="w-4 h-4" /> {t.goals.new}
          </Button>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-48" />)}
          </div>
        ) : goals.length === 0 ? (
          <Card className="border-border shadow-sm">
            <CardContent className="p-10 text-center text-muted-foreground">
              <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">{t.goals.noGoals}</p>
              <p className="text-sm mt-1">{t.goals.createFirst}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowGoalForm(true)}>
                <Plus className="w-4 h-4 mr-1" /> {t.goals.new}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {goals.map((goal) => {
              const target = parseFloat(String(goal.targetAmount));
              const current = parseFloat(String(goal.currentAmount));
              const pct = target > 0 ? Math.min((current / target) * 100, 100) : 0;
              const isCompleted = goal.status === "completed" || pct >= 100;
              const linkedCat = goal.linkedCategoryId
                ? categories.find((c) => c.id === goal.linkedCategoryId)
                : null;
              return (
                <Card key={goal.id} className={cn("border-border shadow-sm overflow-hidden", isCompleted && "opacity-80")}>
                  <div className="h-1.5" style={{ backgroundColor: goal.color ?? GOAL_COLORS[0], opacity: 0.8 }} />
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{goal.icon ?? "🎯"}</span>
                        <div>
                          <p className="font-semibold text-foreground text-sm">{goal.name}</p>
                          {goal.deadline && (
                            <p className="text-xs text-muted-foreground">
                              {ui.goals.deadlinePrefix} {formatDate(goal.deadline, { day: "2-digit", month: "2-digit", year: "numeric" })}
                            </p>
                          )}
                          {linkedCat && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              📂 {categoryName(linkedCat.name)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isCompleted && <CheckCircle2 className="w-4 h-4 income-text" />}
                        <button
                          onClick={() => openEditDialog(goal)}
                          className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                          title={ui.goals.editTitle}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate({ id: goal.id })}
                          className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                          title={ui.goals.deleteTitle}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                        <span>{formatCurrency(current)}</span>
                        <span>{formatCurrency(target)}</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                      <p className="text-xs text-right mt-1 font-medium" style={{ color: goal.color ?? "var(--primary)" }}>
                        {pct.toFixed(0)}% {t.goals.progress}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      {addingTo === goal.id ? (
                        <>
                          <Input
                            type="number"
                            placeholder={ui.goals.amountToAdd}
                            value={addAmount}
                            onChange={(e) => setAddAmount(e.target.value)}
                            className="h-8 text-sm flex-1"
                          />
                          <Button
                            size="sm"
                            className="h-8 px-3"
                            onClick={() => handleAddAmount(goal.id, current)}
                            disabled={updateMutation.isPending}
                          >
                            {updateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK"}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setAddingTo(null)}>
                            ✕
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-8 text-xs"
                          onClick={() => { setAddingTo(goal.id); setAddAmount(""); }}
                          disabled={isCompleted}
                        >
                          <Plus className="w-3 h-3 mr-1" /> {ui.goals.addAmount}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Goal creation dialog */}
      <Dialog open={showGoalForm} onOpenChange={(v) => !v && setShowGoalForm(false)}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Nova Meta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Ícone</Label>
              <div className="flex flex-wrap gap-2">
                {GOAL_ICONS.map((ic) => (
                  <button
                    key={ic}
                    onClick={() => setIcon(ic)}
                    className={cn("w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all", icon === ic ? "ring-2 ring-primary bg-primary/10" : "hover:bg-muted")}
                  >
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Nome da Meta</Label>
              <Input placeholder="Ex: Viagem para Europa" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">{t.goals.targetAmount} ({currencySymbol})</Label>
                <Input type="number" placeholder="5000" value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">{t.goals.currentAmount} ({currencySymbol})</Label>
                <Input type="number" placeholder="0" value={currentAmount} onChange={(e) => setCurrentAmount(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Prazo (opcional)</Label>
              <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Categoria vinculada (opcional)</Label>
              <Select value={linkedCategoryId} onValueChange={setLinkedCategoryId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={ui.goals.manualCategory} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma — atualizar manualmente</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Transferências para esta categoria atualizarão o progresso automaticamente.
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Cor</Label>
              <div className="flex gap-2">
                {GOAL_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={cn("w-7 h-7 rounded-full transition-all", color === c && "ring-2 ring-offset-2 ring-foreground")}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowGoalForm(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={handleCreate} disabled={createMutation.isPending}>
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Criar Meta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={!!editingGoal} onOpenChange={(v) => !v && setEditingGoal(null)}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Editar Meta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Ícone</Label>
              <div className="flex flex-wrap gap-2">
                {GOAL_ICONS.map((ic) => (
                  <button
                    key={ic}
                    onClick={() => setEditIcon(ic)}
                    className={cn("w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all", editIcon === ic ? "ring-2 ring-primary bg-primary/10" : "hover:bg-muted")}
                  >
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Nome da Meta</Label>
              <Input placeholder="Ex: Viagem para Europa" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">{t.goals.targetAmount} ({currencySymbol})</Label>
                <Input type="number" placeholder="5000" value={editTargetAmount} onChange={(e) => setEditTargetAmount(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">{t.goals.currentAmount} ({currencySymbol})</Label>
                <Input type="number" placeholder="0" value={editCurrentAmount} onChange={(e) => setEditCurrentAmount(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Prazo (opcional)</Label>
              <Input type="date" value={editDeadline} onChange={(e) => setEditDeadline(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Categoria vinculada (opcional)</Label>
              <Select value={editLinkedCategoryId || "none"} onValueChange={(v) => setEditLinkedCategoryId(v === "none" ? "" : v)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={ui.goals.manualCategory} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma — atualizar manualmente</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Transferências para esta categoria atualizarão o progresso automaticamente.
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Cor</Label>
              <div className="flex gap-2">
                {GOAL_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setEditColor(c)}
                    className={cn("w-7 h-7 rounded-full transition-all", editColor === c && "ring-2 ring-offset-2 ring-foreground")}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditingGoal(null)}>Cancelar</Button>
              <Button className="flex-1" onClick={handleEditSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TransactionForm open={showForm} onClose={() => setShowForm(false)} />
    </BolsoLayout>
  );
}
